import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

import AboutSummerResidency2025 from "../SummerWorkshop2025/AboutSummerWorkshop2025";
import Application from "../Application/Application";
import WorkshopSchedule from "../WorkshopSchedule/WorkshopSchedule";
import WorkshopMaterials from "../WorkshopMaterials/WorkshopMaterials";
import { Alert } from "@mui/material";
import { useContext } from "react";
import { AuthContext } from "../../components/Context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

const SummerWorkshopArchive2025 = () => {
  useEffect(() => {
    document.title = "Summer Residency 2025 Archive - MS Digital Hub";
  }, []);

  // ---- section refs for "Quick Links" scroll
  const aboutRef = useRef(null);
  const appRef = useRef(null);
  const scheduleRef = useRef(null);
  const materialsRef = useRef(null);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollTo = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ---- accordion control (optional)
  const [open, setOpen] = useState({
    about: true,
    application: true,
    schedule: true,
    materials: false,
  });

  const expandAll = () =>
    setOpen({
      about: true,
      application: true,
      schedule: true,
      materials: true,
    });
  const collapseAll = () =>
    setOpen({
      about: false,
      application: false,
      schedule: false,
      materials: false,
    });

  const archiveMeta = useMemo(
    () => [{ label: "Audience: Faculty / Archivists" }],
    []
  );

  const quickLinks = useMemo(
    () => [
      {
        id: "about",
        title: "About",
        desc: "Overview, who it's for, dates, and guest bios.",
        icon: <InfoOutlinedIcon fontSize="large" />,
        chip: "Overview",
        onClick: () => scrollTo(aboutRef),
      },
      {
        id: "application",
        title: "Application",
        desc: "Qualification, application requirements, timeline, FAQs.",
        icon: <EditNoteOutlinedIcon fontSize="large" />,
        chip: "Deadline Passed",
        onClick: () => scrollTo(appRef),
      },
      {
        id: "schedule",
        title: "Schedule",
        desc: "Day-by-day topics with details.",
        icon: <EventAvailableOutlinedIcon fontSize="large" />,
        chip: "Plan",
        onClick: () => scrollTo(scheduleRef),
      },
      {
        id: "materials",
        title: "Materials",
        desc: "Attendee-only packets, readings, and links (code required).",
        icon: <MenuBookOutlinedIcon fontSize="large" />,
        chip: "Attendees",
        onClick: () => scrollTo(materialsRef),
      },
    ],
    []
  );

  const QuickCard = ({ item }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid #e8e8e8",
        borderRadius: 3,
        background:
          "linear-gradient(180deg, rgba(255,215,0,0.10) 0%, rgba(255,255,255,1) 45%)",
        transition: "transform 0.18s ease, border-color 0.18s ease",
        "&:hover": { transform: "translateY(-3px)", borderColor: "#FFD700" },
      }}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ color: "#111", mt: 0.5 }}>{item.icon}</Box>
          <Chip
            label={item.chip}
            size="small"
            variant="outlined"
            sx={{
              fontSize: 11,
              height: 22,
              borderRadius: "6px",
              color: "#555",
              backgroundColor: "#f5f5f5",
            }}
          />
        </Box>

        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
          {item.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: "#444", lineHeight: 1.7, minHeight: "3.6rem" }}
        >
          {item.desc}
        </Typography>

        <Button
          variant="contained"
          onClick={item.onClick}
          sx={{
            mt: "auto",
            backgroundColor: "#FFD700",
            color: "#111",

            borderRadius: 2,
            "&:hover": { backgroundColor: "#FFB800" },
          }}
        >
          Jump to section
        </Button>
      </CardContent>
    </Card>
  );

  const SectionShell = ({
    id,
    title,
    subtitle,
    refEl,
    expanded,
    onToggle,
    children,
  }) => (
    <Box ref={refEl} sx={{ scrollMarginTop: "110px" }}>
      <Accordion
        expanded={expanded}
        onChange={() => onToggle((p) => !p)}
        elevation={0}
        sx={{
          border: "1px solid #eaeaea",
          borderRadius: 3,
          overflow: "hidden",
          "&:before": { display: "none" },
          mb: 3,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                marginBottom: "5px",
                textAlign: "",
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "#555", mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "auto",
        padding: { xs: "18px", sm: "26px", md: "24px 80px" },
        backgroundColor: "#fff",
        borderRadius: "8px",
      }}
    >
      {/* Archive Header */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 2, fontWeight: 700, color: "#555" }}
        >
          ARCHIVE
        </Typography>

        <Typography
          variant="h4"
          sx={{
            marginBottom: "30px",

            textAlign: "center",
            paddingBottom: "5px",
            borderBottom: "2px solid #FFD700",
          }}
        >
          {" "}
          Summer Residency 2025
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: 900,
            mx: "auto",
            lineHeight: 1.8,
            mt: 1,
            color: "#333",
          }}
        >
          A single archive page with all Summer Residency 2025 content embedded
          below—overview, application details, schedule, and attendee materials.
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          {archiveMeta.map((m) => (
            <Chip
              key={m.label}
              icon={m.icon || undefined}
              label={m.label}
              size="small"
              sx={{
                backgroundColor: "#f6f6f6",
                border: "1px solid #e6e6e6",
                fontWeight: 700,
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Archive Notice + controls */}
      <Box
        sx={{
          border: "1px solid #ffe58f",
          backgroundColor: "#fffbe6",
          borderRadius: 2,
          px: 2,
          py: 1.5,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.7 }}>
          <b>Note:</b> “Workshop Materials” is for attendees only.
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={expandAll}
            sx={{
              borderColor: "#111",
              color: "#111",

              "&:hover": { borderColor: "#000", backgroundColor: "#f5f5f5" },
            }}
          >
            Expand all
          </Button>
          <Button
            variant="outlined"
            onClick={collapseAll}
            sx={{
              borderColor: "#111",
              color: "#111",

              "&:hover": { borderColor: "#000", backgroundColor: "#f5f5f5" },
            }}
          >
            Collapse all
          </Button>
        </Stack>
      </Box>

      {/* Quick Links */}
      <Typography variant="h5" sx={{ mb: 1 }}>
        Quick Links
      </Typography>
      <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
        Jump to a section below (no redirection).
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickLinks.map((q) => (
          <Grid item xs={12} sm={6} md={6} lg={3} key={q.id}>
            <QuickCard item={q} />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Embedded Sections */}
      <SectionShell
        id="about"
        title="About"
        subtitle="Program overview and guest speaker bios."
        refEl={aboutRef}
        expanded={open.about}
        onToggle={(fn) => setOpen((p) => ({ ...p, about: fn(p.about) }))}
      >
        <AboutSummerResidency2025 />
      </SectionShell>

      <SectionShell
        id="application"
        title="Application"
        subtitle="Qualifications, required materials, timeline, and FAQs."
        refEl={appRef}
        expanded={open.application}
        onToggle={(fn) =>
          setOpen((p) => ({ ...p, application: fn(p.application) }))
        }
      >
        {/* Your Application page already has tabs for qualification/application/timeline/FAQs */}
        <Application defaultTab={0} deadline="2025-04-15T23:59:59" />
      </SectionShell>

      <SectionShell
        id="schedule"
        title="Workshop Schedule"
        subtitle="Day-by-day schedule with details."
        refEl={scheduleRef}
        expanded={open.schedule}
        onToggle={(fn) => setOpen((p) => ({ ...p, schedule: fn(p.schedule) }))}
      >
        <WorkshopSchedule />
      </SectionShell>

      <SectionShell
        id="materials"
        title="Workshop Materials"
        subtitle="Attendee-only materials (login required)."
        refEl={materialsRef}
        expanded={open.materials}
        onToggle={(fn) =>
          setOpen((p) => ({ ...p, materials: fn(p.materials) }))
        }
      >
        {!user ? (
          <Alert
            severity="warning"
            sx={{
              border: "1px solid #ffe58f",
              backgroundColor: "#fffbe6",
              borderRadius: 2,
            }}
            action={
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#ffd700",
                  color: "#111",
                  borderRadius: 2,

                  "&:hover": { backgroundColor: "#ffb800" },
                  fontWeight: 700,
                }}
                onClick={() =>
                  navigate("/sign-in", {
                    state: { from: location.pathname, scrollTo: "materials" },
                  })
                }
              >
                Login
              </Button>
            }
          >
            <b>Login required:</b> Workshop Materials are only visible to
            attendees. Please sign in, then return here.
          </Alert>
        ) : (
          <WorkshopMaterials />
        )}
      </SectionShell>
      <Box sx={{ mt: 4, textAlign: "center", opacity: 0.9 }}>
        <Typography variant="caption" sx={{ display: "block", color: "#666" }}>
          Tip: Use the “Quick Links” to jump to sections, and expand/collapse to
          keep the page tidy.
        </Typography>
      </Box>
    </Box>
  );
};

export default SummerWorkshopArchive2025;
